// Centralized Branding System for Multi-Patron Vendor Portal
// This utility provides a single place to manage all branding elements for vendor-facing applications
// Simply change the PATRON_ID to switch between different patron configurations

// Current active patron - Change this to switch branding
const PATRON_ID = process.env.REACT_APP_PATRON_ID || 'sfa';

const PUBLIC_URL = process.env.PUBLIC_URL || '';

const resolvePublicPath = (path) => {
  if (!path) return path;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  if (!PUBLIC_URL) {
    return path;
  }
  const normalizedBase = PUBLIC_URL.endsWith('/') ? PUBLIC_URL.slice(0, -1) : PUBLIC_URL;
  return `${normalizedBase}${path}`;
};

// Patron-specific branding configurations for vendor portal
const BRANDING_CONFIG = {
  sda: {
    // Company Information
    company: {
      name: 'SDA',
      fullName: 'Sure Drive Assistance',
      tagline: 'Vendor Portal',
      description: 'Vendor Management System'
    },
    
    // Logo and Image Paths
    images: {
      // Main logo used throughout the app
      logo: '/img/sda/sda-logo.png',
      // Sidebar logos for different states
      sidebarExpanded: '/img/sda/sda-logo.png',     // Full logo when sidebar is open
      sidebarCollapsed: '/img/sda/sda-login.png',    // Icon/compact logo when sidebar is collapsed
      // Login specific logo
      loginLogo: '/img/sda/sda-login-logo.png',
      // Favicon and PWA icons
      favicon: '/img/sda/favicon.ico',
      appleTouchIcon: '/img/sda/logo192.png',
      logo512: '/img/sda/logo512.png',
      // Arrow icons for UI elements
      arrowDefault: '/img/sda/arrow.png',
      arrowWhite: '/img/sda/arrow-white.png',
      arrowBlack: '/img/sda/arrow-black.png'
    },
    
    // Color Theme
    colors: {
      // Primary brand colors
      primary: '#0496FF',
      primaryHover: '#1d4ed8',
      primaryLight: '#E2E1FF',
      
      // Secondary colors
      secondary: '#15283C',
      secondaryLight: '#667A8A',
      
      // Background colors
      sidebarBg: '#15283c',
      mainBg: '#15283C',
      contentBg: '#F9FAFB',
      navBg: '#1A232D',
      
      // Status colors
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
      
      // Gradient colors
      gradientFrom: '#667eea',
      gradientTo: '#764ba2'
    },
    
    // Typography
    typography: {
      fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif`,
      headingFont: 'inherit',
      bodyFont: 'inherit'
    },
    
    // Application specific settings
    app: {
      title: 'Vendor Portal',
      shortTitle: 'Vendor Portal',
      subtitle: 'Vendor Management'
    }
  },
  
  sfa: {
    company: {
      name: 'SFA',
      fullName: 'SoftForce Assistance',
      tagline: 'Vendor Portal',
      description: 'Vendor Management System'
    },
    
    images: {
      logo: '/img/sfa/sfa-logo.png',
      sidebarExpanded: '/img/sfa/sfa-logo.png',
      sidebarCollapsed: '/img/sfa/sfa-login.png',
      loginLogo: '/img/sfa/sfa-logo.png',
      favicon: '/img/sfa/sfa-favicon.ico',
      appleTouchIcon: '/img/sfa/logo192.png',
      logo512: '/img/sfa/logo512.png',
      arrowDefault: '/img/sfa/arrow.png',
      arrowWhite: '/img/sfa/arrow-white.png',
      arrowBlack: '/img/sfa/arrow-black.png'
    },
    
    colors: {
      primary: '#FF6B35',
      primaryHover: '#E55A2B',
      primaryLight: '#FFF2E6',
      secondary: '#2C3E50',
      secondaryLight: '#7F8C8D',
      sidebarBg: '#2C3E50',
      mainBg: '#2C3E50',
      contentBg: '#F8F9FA',
      navBg: '#34495E',
      success: '#27AE60',
      warning: '#F39C12',
      error: '#E74C3C',
      info: '#3498DB',
      gradientFrom: '#FF6B35',
      gradientTo: '#F7931E'
    },
    
    typography: {
      fontFamily: `'Inter', -apple-system, BlinkMacSystemFont, sans-serif`,
      headingFont: `'Inter', sans-serif`,
      bodyFont: `'Inter', sans-serif`
    },
    
    app: {
      title: 'SFA Vendor Portal',
      shortTitle: 'SFA Vendor',
      subtitle: 'Vendor Management'
    }
  }
};

/**
 * Get the current patron's branding configuration
 * @returns {Object} Current patron's branding config
 */
export const getCurrentBranding = () => {
  const config = BRANDING_CONFIG[PATRON_ID];
  if (!config) {
    console.warn(`Branding config not found for patron: ${PATRON_ID}. Falling back to 'sda' config.`);
    return BRANDING_CONFIG.sda;
  }
  return config;
};

/**
 * Get the current patron ID
 * @returns {string} Current patron ID
 */
export const getCurrentPatronId = () => PATRON_ID;

/**
 * Get a specific branding value using dot notation
 * @param {string} path - Path to the value (e.g., 'colors.primary', 'images.logo')
 * @param {any} defaultValue - Default value if path not found
 * @returns {any} The branding value
 */
export const getBrandingValue = (path, defaultValue = null) => {
  const config = getCurrentBranding();
  const keys = path.split('.');
  let value = config;
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return defaultValue;
    }
  }
  
  return value;
};

/**
 * Get all image paths for the current patron
 * @returns {Object} Image configuration object
 */
export const getImages = () => {
  const images = getCurrentBranding().images;
  
  // Ensure all paths are properly resolved for public access
  const resolvedImages = {};
  for (const [key, path] of Object.entries(images)) {
    // If path starts with /src/, it's an invalid public path - log warning
    if (path.startsWith('/src/')) {
      console.warn(`Invalid image path detected: ${path}. Images in /src/ are not publicly accessible. Please move to /public/ directory.`);
      // Try to find alternative in public
      const filename = path.split('/').pop();
      resolvedImages[key] = `/${filename}`;
    } else {
      resolvedImages[key] = resolvePublicPath(path);
    }
  }
  
  return resolvedImages;
};

/**
 * Get sidebar logo based on sidebar state
 * @param {boolean} isExpanded - Whether sidebar is expanded
 * @returns {string} Logo path
 */
export const getSidebarLogo = (isExpanded = true) => {
  const images = getImages();
  return isExpanded ? images.sidebarExpanded : images.sidebarCollapsed;
};

/**
 * Get all colors for the current patron
 * @returns {Object} Color configuration object
 */
export const getColors = () => getCurrentBranding().colors;

/**
 * Get company information for the current patron
 * @returns {Object} Company information object
 */
export const getCompanyInfo = () => getCurrentBranding().company;

/**
 * Get app-specific settings for the current patron
 * @returns {Object} App configuration object
 */
export const getAppConfig = () => getCurrentBranding().app;

/**
 * Get typography settings for the current patron
 * @returns {Object} Typography configuration object
 */
export const getTypography = () => getCurrentBranding().typography;

/**
 * Generate CSS custom properties for the current patron's theme
 * @returns {Object} CSS custom properties object
 */
export const getCSSCustomProperties = () => {
  const colors = getColors();
  const typography = getTypography();
  const images = getImages();
  
  return {
    '--brand-primary': colors.primary,
    '--brand-primary-hover': colors.primaryHover,
    '--brand-primary-light': colors.primaryLight,
    '--brand-secondary': colors.secondary,
    '--brand-secondary-light': colors.secondaryLight,
    '--brand-sidebar-bg': colors.sidebarBg,
    '--brand-main-bg': colors.mainBg,
    '--brand-content-bg': colors.contentBg,
    '--brand-nav-bg': colors.navBg,
    '--brand-success': colors.success,
    '--brand-warning': colors.warning,
    '--brand-error': colors.error,
    '--brand-info': colors.info,
    '--brand-gradient-from': colors.gradientFrom,
    '--brand-gradient-to': colors.gradientTo,
    '--brand-font-family': typography.fontFamily,
    '--brand-heading-font': typography.headingFont,
    '--brand-body-font': typography.bodyFont,
    '--brand-arrow-default': `url(${images.arrowDefault})`,
    '--brand-arrow-white': `url(${images.arrowWhite})`,
    '--brand-arrow-black': `url(${images.arrowBlack})`
  };
};

/**
 * Generate inline styles for branded elements
 * @param {string} type - Type of element ('button', 'background', 'text', etc.)
 * @param {string} variant - Variant of the element ('primary', 'secondary', etc.)
 * @returns {Object} Inline styles object
 */
export const getBrandedStyles = (type, variant = 'primary') => {
  const colors = getColors();
  const typography = getTypography();
  
  const styleMap = {
    button: {
      primary: {
        backgroundColor: colors.primary,
        color: '#ffffff',
        border: `1px solid ${colors.primary}`,
        fontFamily: typography.fontFamily
      },
      secondary: {
        backgroundColor: colors.secondary,
        color: '#ffffff',
        border: `1px solid ${colors.secondary}`,
        fontFamily: typography.fontFamily
      }
    },
    background: {
      primary: {
        backgroundColor: colors.primary
      },
      secondary: {
        backgroundColor: colors.secondary
      },
      gradient: {
        background: `linear-gradient(135deg, ${colors.gradientFrom} 0%, ${colors.gradientTo} 100%)`
      }
    },
    text: {
      primary: {
        color: colors.primary,
        fontFamily: typography.fontFamily
      },
      secondary: {
        color: colors.secondary,
        fontFamily: typography.fontFamily
      }
    }
  };
  
  return styleMap[type]?.[variant] || {};
};

/**
 * Create a branded className string
 * @param {string} baseClass - Base CSS class
 * @param {string} brandModifier - Brand-specific modifier
 * @returns {string} Combined className
 */
export const getBrandedClassName = (baseClass, brandModifier = '') => {
  const patronId = getCurrentPatronId();
  const modifier = brandModifier ? `-${brandModifier}` : '';
  return `${baseClass} ${baseClass}--${patronId}${modifier}`;
};

// Export the full configuration for advanced use cases
export { BRANDING_CONFIG };

// Default export for convenience
const brandingUtils = {
  getCurrentBranding,
  getCurrentPatronId,
  getBrandingValue,
  getImages,
  getSidebarLogo,
  getColors,
  getCompanyInfo,
  getAppConfig,
  getTypography,
  getCSSCustomProperties,
  getBrandedStyles,
  getBrandedClassName
};

export default brandingUtils; 